import React from 'react';
import type { Channel } from '../types';
import { channelDisplayName } from '../lib/channelDisplay';
import styles from './ChannelRail.module.css';

interface Props {
  channel: Channel;
  active: boolean;
  /** Retained for callers while ordinary channel rows no longer support reorder. */
  isOwner?: boolean;
  onClick: () => void;
  /** Personal pin context menu trigger; DM rows must not pass this. */
  onContextMenu?: (event: React.MouseEvent) => void;
  /** Personal pinned indicator — a pin is a negative layout position. */
  pinned?: boolean;
}

function ChannelVisibilityMarker({ isArchived, isPrivate }: { isArchived: boolean; isPrivate: boolean }) {
  const base = isArchived ? '📦' : '#';
  const className = `channel-hash${isPrivate ? ' channel-private-indicator' : ''}`;
  return (
    <span
      className={className}
      data-private-indicator={isPrivate ? 'true' : undefined}
      aria-label={isPrivate ? '私有频道' : undefined}
      title={isPrivate ? '私有频道' : undefined}
    >
      <span className="channel-hash-base">{base}</span>
      {isPrivate && (
        <svg className="channel-lock-badge" data-private-lock="true" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor" focusable="false">
          <path d="M12 2a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V7a5 5 0 1 1 6 0v3H9V7z" />
        </svg>
      )}
    </span>
  );
}

/** A stable Server channel row. Reordering is intentionally not an interaction. */
export default function ChannelRow({ channel, active, onClick, onContextMenu, pinned }: Props) {
  const unread = channel.unread_count ?? 0;
  const isPrivate = channel.visibility === 'private';
  const isMember = channel.is_member !== false;
  const isArchived = channel.archived_at != null;

  return (
    <button
      type="button"
      className={`channel-item ${styles.item} ${active ? 'channel-item-active' : ''} ${!isMember ? 'channel-item-preview' : ''} ${isArchived ? 'channel-item-archived' : ''} ${pinned ? 'channel-item-pinned' : ''}`}
      onClick={onClick}
      onContextMenu={onContextMenu}
      data-kind="channel"
      data-archived={isArchived ? 'true' : undefined}
      data-private={isPrivate && !isArchived ? 'true' : undefined}
      data-pinned={pinned ? 'true' : undefined}
    >
      <ChannelVisibilityMarker isArchived={isArchived} isPrivate={isPrivate} />
      <span className="channel-name">{channelDisplayName(channel)}</span>
      {pinned && <span className="channel-pinned-indicator" title="已置顶">📌</span>}
      {isArchived && <span className="archived-badge" title="已归档">已归档</span>}
      {!isMember && !isPrivate && !isArchived && <span className="preview-badge">只读</span>}
      {unread > 0 && isMember && !isArchived && <span className="unread-badge">{unread > 99 ? '99+' : unread}</span>}
    </button>
  );
}

/** Compatibility export for callers that render a non-member public row. */
export function ChannelItemStatic(props: Omit<Props, 'isOwner'>) {
  return <ChannelRow {...props} />;
}
